# Quickstart

This guide shows you how to give your MCP server a basic flow in under 5 minutes.

## Install

```bash
npm install taias-sdk
```

---

## 1. Define a Flow

Create `src/taias/flows.ts`:

```ts
export const musicDiscoveryFlow = defineFlow("music_discovery_flow", (flow) => {
  // Logic statements: match condition -> decision
  flow.step(
    { toolName: { is: "get_recent_tracks" } },
    { nextTool: "get_recommendations" },
  );
  flow.step(
    { toolName: { is: "get_recommendations" } },
    { nextTool: "create_playlist" },
  );

  // Add more steps over time:
  // flow.step({ toolName: { is: "create_playlist" } }, { nextTool: "add_collaborator" });
});
```

Each call to `flow.step()` defines a logic statement. The first argument is a `MatchCondition` object describing when the step applies, and the second is the decision to produce when matched. Each field in the match condition uses an explicit operator object (`{ is: ... }` or `{ isNot: ... }`). Steps can match on `toolName`, `params`, `result`, or any combination. See [Match Conditions](/docs#flows-and-decisions) for details.

> **Backwards compatibility:** Handler functions are also supported. They receive the full `TaiasContext` (including `params` and `result`) and can produce decisions dynamically. Logic statements are the recommended approach.

---

## 2. Create a Taias instance

Create `src/taias/instance.ts`:

```ts
export const taias = createTaias({
  flow: musicDiscoveryFlow,
  devMode: true, // Enable validation and warnings during development
  onMissingStep: ({ toolName }) => {
    console.warn(`[Taias] No step defined for tool "${toolName}"`);
  },
});
```

---

## 3. Use Taias in your tools

Call `taias.resolve()` and **append the advice to your message content**. This ensures the LLM receives guidance on which tool to call next. You can pass `params` (the tool's input parameters) and `result` (the tool's execution output) as additional context for the decision engine.

```ts
server.registerTool(
  "get_recent_tracks",
  {
    /* ... */
  },
  async (input, ctx) => {
    const tracks = await fetchRecentTracks(input);

    const affordances = await taias.resolve({
      toolName: "get_recent_tracks",
      params: input, // Tool input parameters
      result: { trackCount: tracks.length }, // Tool execution output
    });

    let message = `Found ${tracks.length} recently played tracks.`;

    // Append Taias advice to guide the LLM to the next step
    if (affordances?.advice) {
      message += `\n\n${affordances.advice}`;
    }

    return {
      content: [
        {
          type: "text",
          text: message, // The advice is included here
        },
      ],
      // ...
    };
  },
);
```

**What does `affordances.advice` contain?**

Auto-generated text that guides the LLM to suggest the next tool in your flow:

```
"FOR THE BEST USER EXPERIENCE, TELL THE USER TO USE THE get_recommendations TOOL NEXT!!!!!"
```

> **Why the formatting?** The emphasis (caps, punctuation) helps LLMs prioritize it.

---

## 4. Add UI Affordances (Optional)

If your tools return widgets, you can also let Taias select which UI components to show.

### Define affordances for your widget

Create `src/taias/affordances.ts`:

```ts
export const trackListAffordances = defineAffordances((r) => {
  // Multiple options for the primary CTA slot
  r.primaryCta("tracks.cta.recommend", { toolName: "get_recommendations" });
  r.primaryCta("tracks.cta.playlist", { toolName: "create_playlist" });
});

export const registry = mergeAffordances([trackListAffordances]);
```

### Pass the registry to createTaias

Update `src/taias/instance.ts`:

```ts
export const taias = createTaias({
  flow: musicDiscoveryFlow,
  affordances: registry, // Add this
  devMode: true,
});
```

### Access selections in your tool

```ts
const affordances = await taias.resolve({ toolName: "get_recent_tracks" });

return {
  content: [{ type: "text", text: message }],
  structuredContent: {
    _widget: "track-list",
    tracks,
    // Pass selections to your widget
    taias: { selections: affordances?.selections ?? {} },
  },
};
```

### Render by handleId in your widget

```tsx
const selections = toolResult?.structuredContent?.taias?.selections ?? {};

if (selections.primaryCta?.handleId === "tracks.cta.recommend") {
  return (
    <Button onClick={() => callTool("get_recommendations")}>
      Get Recommendations
    </Button>
  );
}
```

When your flow changes, the widget automatically shows the right CTA.

See [UI Affordances](/docs#ui-affordances) for the complete guide.
